import { fork, put, take, takeEvery, select } from 'redux-saga/effects';
import { LOCATION_CHANGE, push } from 'connected-react-router';
import queryString from 'query-string';

import { Logger } from 'utils/Logger';

import { IntegrationType } from 'enums/integration-type';
import { MonitorWizzardPaths } from 'enums/monitor-wizzard-paths';

import {
  COLLECTIVE_ACTIVITIES_MODAL_KEY,
  MONITOR_ADD_CLIENT_WIZZARD_MODAL_KEY,
  MONITOR_CREATE_CLIENT_WIZZARD_MODAL_KEY,
  QUESTIONNAIRE_MODAL_KEY,
  CAPITAL_REPORT_MODAL_KEY,
} from 'constants';

import { makeSelectIntegrationFilePayload } from 'reducers/monitor/connect-wizzard/selectors';

import { openCollectiveActivitiesModal } from '../activities/reducer';
import {
  openMonitorWizzard,
  requestClient,
  setMonitorWizzardRedirectClientId,
  setMonitorWizzardAccountingSolution,
  openMonitorWizzardDataLoadingErrorModal,
  triggerSnapshotCreation,
  addConnectionHistoryListItem,
} from '../monitor/reducer';

import { openQuestionnaire } from '../user/actions';

import { TRIGGER_XERO_UPLOAD } from '../monitor/connect-wizzard/types';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';
import { Analytics } from 'utils/Analytics';

const logger = new Logger('Saga:ModalTrigger');

function* triggerSnapshot(id, integration) {
  const xeroPayload = yield select(makeSelectIntegrationFilePayload());
  yield put(triggerSnapshotCreation(id, null, xeroPayload || { provider: integration }));
  yield put(addConnectionHistoryListItem({ id: parseInt(id, 10), integration }));
}

function* openWizard(id, integration, path, options, error) {
  yield put(setMonitorWizzardRedirectClientId(parseInt(id, 10)));
  yield put(requestClient(parseInt(id, 10)));
  yield put(setMonitorWizzardAccountingSolution(integration));
  yield put(addConnectionHistoryListItem({ id: parseInt(id, 10), integration }));

  yield put(openMonitorWizzard(path, options));
  if (error) {
    yield put(openMonitorWizzardDataLoadingErrorModal());
  }
}

export default function* UrlModalTriggerSaga() {
  const action = yield take(LOCATION_CHANGE);

  // Only trigger on the dashboard or monitor or funding for business questionnaire
  const allowedRedirectPaths = ['/dashboard', '/funding', '/monitor'];
  if (!allowedRedirectPaths.includes(action.payload.location.pathname)) {
    return;
  }

  const modalKey = queryString.parse(action.payload.location.search).open;
  const { objectId, error, integration, type, capitalReport } = queryString.parse(
    action.payload.location.search,
  );

  if (
    [
      MONITOR_ADD_CLIENT_WIZZARD_MODAL_KEY,
      MONITOR_CREATE_CLIENT_WIZZARD_MODAL_KEY,
      CAPITAL_REPORT_MODAL_KEY,
    ].includes(modalKey)
  ) {
    Analytics.track(AnalyticsEventCategory.monitor, AnalyticsEventAction.connectRedirect, {
      extraParams: {
        accountingProvider: integration,
      },
    });
  }

  modalKey && logger.info('Triggering modal from search param key', modalKey);

  switch (modalKey) {
    case COLLECTIVE_ACTIVITIES_MODAL_KEY:
      yield put(openCollectiveActivitiesModal());
      break;

    case MONITOR_ADD_CLIENT_WIZZARD_MODAL_KEY:
      if (!objectId) break;
      yield fork(
        openWizard,
        objectId,
        IntegrationType[integration],
        MonitorWizzardPaths.addClient,
        { defaultStartingStep: 0 },
        error,
      );
      break;

    case MONITOR_CREATE_CLIENT_WIZZARD_MODAL_KEY:
      if (!objectId) break;
      yield fork(
        openWizard,
        objectId,
        IntegrationType[integration],
        MonitorWizzardPaths.createClient,
        { defaultStartingStep: 2 },
        error,
      );
      break;

    case QUESTIONNAIRE_MODAL_KEY:
      if (objectId) {
        yield put(
          openQuestionnaire({
            profileId: objectId,
            shouldGetSnapshot: true,
            step: 'financial-details',
            reopenType: type,
          }),
        );
      }

      break;

    case CAPITAL_REPORT_MODAL_KEY:
      if (capitalReport && integration) {
        yield fork(triggerSnapshot, capitalReport, IntegrationType[integration]);
      }

      break;

    default:
      break;
  }

  const searchParams =
    modalKey === CAPITAL_REPORT_MODAL_KEY
      ? `?capitalReport=${capitalReport}`
      : action.payload.location.search;

  yield put(push({ pathname: action.payload.pathname, search: searchParams }));
}

function* triggerXeroUpload({ entityId, startPath }) {
  const defaultStartingStep = startPath === MonitorWizzardPaths.addClient ? 0 : 2;
  if (entityId) {
    yield fork(openWizard, entityId, IntegrationType.xero, MonitorWizzardPaths.addClient, {
      defaultStartingStep,
    });
  }
}

export function* XeroModalTriggerSaga() {
  yield takeEvery(TRIGGER_XERO_UPLOAD, triggerXeroUpload);
}
